import { type ReactNode, useContext, useMemo, useState } from 'react';

import { ContextMenu } from '@base-ui/react/context-menu';
import type { CustomDetailCellRendererProps } from 'ag-grid-react';
import { CirclePlus, Pencil, Trash2, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  defaultGradingRows,
  type GradingGridRow,
  GradingMatrix,
  gridToStageGradeCount,
  StatusBadge,
  sumGradingRows,
} from '@/components/common';
import { type ColumnDef, DataTable } from '@/components/common/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { PrimaryContext } from '@/services/context';
import {
  collectVersions,
  isOnFarmFreezingSelectionEligible,
  type OnFarmImplantFreshEmbryo,
  type OnFarmImplantMatingAllocation,
  OnFarmImplantRequestError,
  toRecordKey,
  useAddOnFarmImplantQueue,
  useCreateOnFarmImplantFreshEmbryos,
  useSaveOnFarmImplantFreshDegenerates,
} from '@/services/on-farm-implant';

import { isOwnEmbryoEditLock } from '../../hooks/useEmbryoEditLock';
import { SendToFreezingDialog } from '../OnFarmFreezingTab/SendToFreezingDialog';
import type { FreshMatingRow } from './columnDefs';
import { EditFreshEmbryosDialog } from './EditFreshEmbryosDialog';
import {
  type FreshEmbryoAction,
  FreshEmbryoActionDialog,
} from './FreshEmbryoActionDialog';
import { type GradeFilter, useFreshTab } from './FreshTabContext';
import { MatingAllocationTable } from './MatingAllocationTable';

const queueStateColors = {
  Loaded: { bg: 'bg-blue-100', text: 'text-blue-700' },
  Implanted: { bg: 'bg-green-100', text: 'text-green-700' },
  Discarded: { bg: 'bg-red-100', text: 'text-red-700' },
  OnFarmFreezing: { bg: 'bg-sky-100', text: 'text-sky-700' },
};

const emptyGradingRows = () => defaultGradingRows.map((row) => ({ ...row }));

const toMessage = (error: unknown, fallback: string) =>
  error instanceof OnFarmImplantRequestError && error.problems.length
    ? error.problems.join(' ')
    : fallback;

type DegeneratesFieldProps = {
  inputId: string;
  sessionId: string;
  matingId: string;
  matingName: string;
  saved: number | null | undefined;
  version: string | null | undefined;
  disabled: boolean;
  autoCalculated: boolean;
};

const DegeneratesField = ({
  inputId,
  sessionId,
  matingId,
  matingName,
  saved,
  version,
  disabled,
  autoCalculated,
}: DegeneratesFieldProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(saved == null ? '' : String(saved));
  const { mutateAsync: saveDegenerates, isPending } =
    useSaveOnFarmImplantFreshDegenerates();
  const locked = disabled || autoCalculated;

  const handleSave = async () => {
    try {
      await saveDegenerates({
        sessionId,
        matingId,
        degenerates: Number(value),
        version,
      });
      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.fresh.degenerates.saved', {
          mating: matingName,
        }),
      });
    } catch (saveError) {
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.fresh.degenerates.saveFailed'),
        description: toMessage(saveError, ''),
      });
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className={outcomeLabelClass}>
        {t('onFarmImplant.fresh.degenerates.label')}
      </label>
      <div className="flex items-center gap-1.5">
        <Input
          id={inputId}
          type="text"
          inputMode="numeric"
          value={value}
          disabled={locked}
          onChange={(event) => setValue(event.target.value.replace(/\D/g, ''))}
          className="h-8 min-w-0 flex-1"
        />
        <Button
          variant="outline"
          size="sm"
          disabled={
            locked || isPending || value === '' || Number(value) === saved
          }
          onClick={() => void handleSave()}
        >
          {t('onFarmImplant.fresh.degenerates.save')}
        </Button>
      </div>
      {autoCalculated && (
        <span className="text-muted-foreground text-[11px]">
          {t('onFarmImplant.fresh.degenerates.autoHint')}
        </span>
      )}
    </div>
  );
};

const outcomeLabelClass = 'text-sm font-medium';

type ReadOnlyOutcomeProps = { id: string; label: string; value: number };

const ReadOnlyOutcome = ({ id, label, value }: ReadOnlyOutcomeProps) => (
  <div className="flex flex-col gap-1">
    <label htmlFor={id} className={outcomeLabelClass}>
      {label}
    </label>
    <Input
      id={id}
      type="text"
      value={value}
      readOnly
      tabIndex={-1}
      className="bg-muted text-muted-foreground h-8 cursor-default focus-visible:ring-0"
    />
  </div>
);

type MatingOutcomesProps = {
  matingKey: string;
  allocation: OnFarmImplantMatingAllocation | null | undefined;
  degenerates: ReactNode;
};

const MatingOutcomes = ({
  matingKey,
  allocation,
  degenerates,
}: MatingOutcomesProps) => {
  const { t } = useTranslation();
  const discards = [
    allocation?.onFarmDiscardedGrade1 ?? 0,
    allocation?.onFarmDiscardedGrade2 ?? 0,
    allocation?.onFarmDiscardedGrade3 ?? 0,
  ];

  return (
    <div className="grid max-w-4xl grid-cols-2 items-start gap-4 sm:grid-cols-5">
      {discards.map((count, index) => (
        <ReadOnlyOutcome
          key={index}
          id={`discards-grade-${index + 1}-${matingKey}`}
          label={t('onFarmImplant.fresh.discardsByGrade', { grade: index + 1 })}
          value={count}
        />
      ))}
      {degenerates}
      <div className="flex flex-col gap-1">
        <span className={outcomeLabelClass}>
          {t('onFarmImplant.fresh.totalDiscarded')}
        </span>
        <span className="border-input bg-background flex h-8 items-center rounded-md border px-3 text-sm font-semibold">
          {discards.reduce((sum, count) => sum + count, 0)}
        </span>
      </div>
    </div>
  );
};

export const FreshMatingDetail = ({
  data: mating,
}: CustomDetailCellRendererProps<FreshMatingRow>) => {
  const { t } = useTranslation();
  const { themeMode } = useContext(PrimaryContext);
  const {
    sessionId,
    readOnly,
    embryos,
    nextStrawNumber,
    setNextStrawNumber,
    gradingByMating,
    setMatingGrading,
    gradeFilterByMating,
    setMatingGradeFilter,
    autoDiscardGrade2,
    autoDiscardGrade3,
    autoCalculateDegenerates,
  } = useFreshTab();
  const matingKey = mating ? toRecordKey(mating.id, mating.accountId) : '';
  const gradingRows = gradingByMating[matingKey] ?? defaultGradingRows;
  const setGradingRows = (rows: GradingGridRow[]) =>
    setMatingGrading(matingKey, rows);
  const gradeFilter = gradeFilterByMating[matingKey] ?? 'all';
  const setGradeFilter = (filter: GradeFilter) =>
    setMatingGradeFilter(matingKey, filter);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [freezingOpen, setFreezingOpen] = useState(false);
  const [embryoAction, setEmbryoAction] = useState<FreshEmbryoAction | null>(
    null,
  );
  const [menuEmbryo, setMenuEmbryo] = useState<OnFarmImplantFreshEmbryo | null>(
    null,
  );
  const { mutateAsync: createEmbryos, isPending: isCreating } =
    useCreateOnFarmImplantFreshEmbryos();
  const { mutateAsync: queueEmbryos, isPending: isQueueing } =
    useAddOnFarmImplantQueue();

  const matingEmbryos = useMemo(
    () =>
      mating
        ? embryos.filter(
            (embryo) =>
              embryo.matingId === mating.id &&
              (embryo.accountId ?? null) === (mating.accountId ?? null),
          )
        : [],
    [embryos, mating],
  );
  const filteredEmbryos = useMemo(
    () =>
      matingEmbryos.filter(
        (embryo) =>
          gradeFilter === 'all' || embryo.grade === Number(gradeFilter),
      ),
    [matingEmbryos, gradeFilter],
  );
  const loadedEmbryos = matingEmbryos.filter(
    (embryo) => embryo.queueState === 'Loaded',
  );
  const selectableIds = useMemo(
    () =>
      readOnly ? [] : filteredEmbryos.map((embryo) => embryo.sessionEmbryoId),
    [filteredEmbryos, readOnly],
  );
  const selectedLoadedEmbryos = matingEmbryos.filter(
    (embryo) =>
      (embryo.queueState === 'Loaded' || embryo.queueState === 'Discarded') &&
      selectedIds.includes(embryo.sessionEmbryoId),
  );
  const selectedKeptEmbryos = matingEmbryos.filter(
    (embryo) =>
      embryo.queueState === 'Loaded' &&
      selectedIds.includes(embryo.sessionEmbryoId),
  );
  const selectedDiscardedEmbryos = matingEmbryos.filter(
    (embryo) =>
      embryo.queueState === 'Discarded' &&
      embryo.strawNumber === null &&
      selectedIds.includes(embryo.sessionEmbryoId),
  );
  const selectedEmbryos = matingEmbryos.filter((embryo) =>
    selectedIds.includes(embryo.sessionEmbryoId),
  );
  const canSendToFreezing = isOnFarmFreezingSelectionEligible(selectedEmbryos);
  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.includes(id));
  const embryoTotal = sumGradingRows(gradingRows);
  const keptTotal = gridToStageGradeCount(gradingRows)
    .filter(
      (entry) =>
        !(entry.grade === 2 && autoDiscardGrade2) &&
        !(entry.grade === 3 && autoDiscardGrade3),
    )
    .reduce((sum, entry) => sum + entry.count, 0);

  const columns = useMemo<ColumnDef<OnFarmImplantFreshEmbryo>[]>(
    () => [
      {
        key: 'select',
        width: 36,
        reorderable: false,
        hideable: false,
        chooserLabel: t('onFarmImplant.fresh.select'),
        header: (
          <input
            type="checkbox"
            aria-label={t('onFarmImplant.fresh.selectAll')}
            checked={allSelected}
            disabled={selectableIds.length === 0}
            onChange={(event) =>
              setSelectedIds(event.target.checked ? selectableIds : [])
            }
          />
        ),
        render: (embryo) => (
          <input
            type="checkbox"
            aria-label={t('onFarmImplant.fresh.selectStraw', {
              straw: embryo.strawNumber ?? '—',
            })}
            checked={selectedIds.includes(embryo.sessionEmbryoId)}
            disabled={readOnly}
            onChange={(event) =>
              setSelectedIds((current) =>
                event.target.checked
                  ? [...current, embryo.sessionEmbryoId]
                  : current.filter((id) => id !== embryo.sessionEmbryoId),
              )
            }
          />
        ),
      },
      {
        key: 'strawNumber',
        header: t('onFarmImplant.fresh.straw'),
        width: 90,
        align: 'right',
        sortable: true,
        render: (embryo) => embryo.strawNumber ?? '—',
      },
      {
        key: 'stage',
        header: t('onFarmImplant.fresh.stage'),
        width: 70,
        align: 'right',
        sortable: true,
        render: (embryo) =>
          embryo.stage ?? t('onFarmImplant.fresh.unknownStage'),
      },
      {
        key: 'grade',
        header: t('onFarmImplant.fresh.gradeLabel'),
        width: 70,
        align: 'right',
        sortable: true,
        render: (embryo) => embryo.grade,
      },
      {
        key: 'matingName',
        header: t('onFarmImplant.fresh.mating'),
        width: 140,
        sortable: true,
        render: (embryo) => (
          <span className="block truncate" title={embryo.matingName ?? ''}>
            {embryo.matingName}
          </span>
        ),
      },
      {
        key: 'donorName',
        header: t('onFarmImplant.fresh.donor'),
        width: 140,
        sortable: true,
        render: (embryo) => embryo.donorName,
      },
      {
        key: 'sireName',
        header: t('onFarmImplant.fresh.sire'),
        width: 140,
        sortable: true,
        render: (embryo) => embryo.sireName,
      },
      {
        key: 'accountName',
        header: t('onFarmImplant.fresh.account'),
        width: 220,
        sortable: true,
        render: (embryo) => embryo.accountName,
      },
      {
        key: 'queueState',
        header: t('onFarmImplant.fresh.status'),
        width: 110,
        align: 'right',
        sortable: true,
        render: (embryo) => {
          const label =
            embryo.queueState === 'OnFarmFreezing'
              ? t('onFarmImplant.fresh.onFarmFreezing')
              : embryo.queueState;
          return (
            <span className="inline-flex items-center gap-1">
              {embryo.editingBy &&
                !isOwnEmbryoEditLock(embryo.editingLockId) && (
                  <span className="rounded bg-amber-100 px-1.5 text-[11px] text-amber-800">
                    {t('onFarmImplant.fresh.edit.badge', {
                      name: embryo.editingBy,
                    })}
                  </span>
                )}
              <StatusBadge
                label={label}
                colorMap={{ [label]: queueStateColors[embryo.queueState] }}
              />
            </span>
          );
        },
      },
    ],
    [t, allSelected, selectableIds, selectedIds, readOnly],
  );

  if (!mating) return null;

  const handleCreate = async () => {
    if (!sessionId || readOnly) return;
    if (!nextStrawNumber) {
      setError(t('onFarmImplant.fresh.strawRequired'));
      return;
    }
    if (embryoTotal === 0) {
      setError(t('onFarmImplant.fresh.countRequired'));
      return;
    }

    try {
      await createEmbryos({
        sessionId,
        input: {
          matingId: mating.id,
          accountId: mating.accountId,
          startingStrawNumber: Number(nextStrawNumber),
          entries: gridToStageGradeCount(gradingRows),
          autoDiscardGrade2,
          autoDiscardGrade3,
        },
      });
      setNextStrawNumber(String(Number(nextStrawNumber) + keptTotal));
      setGradingRows(emptyGradingRows());
      setError(null);
    } catch (createError) {
      setError(toMessage(createError, t('onFarmImplant.fresh.createFailed')));
    }
  };

  const handleQueue = async (addAll: boolean) => {
    if (!sessionId || readOnly) return;
    try {
      const embryosToQueue = addAll ? loadedEmbryos : selectedLoadedEmbryos;
      const response = await queueEmbryos({
        sessionId,
        sourceType: 'fresh',
        input: {
          items: embryosToQueue.map((embryo) => ({
            sessionEmbryoId: embryo.sessionEmbryoId,
            transferStrawNumber: embryo.strawNumber,
          })),
          versions: collectVersions(embryosToQueue),
        },
      });
      setSelectedIds([]);
      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.fresh.queueSuccessTitle'),
        description: addAll
          ? t('onFarmImplant.fresh.queueAllSuccess', {
              count: response.totalQueued,
              mating: mating.recordName ?? mating.id,
            })
          : t('onFarmImplant.fresh.queueSelectedSuccess', {
              count: response.totalQueued,
            }),
      });
    } catch (queueError) {
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.fresh.queueFailed'),
        description: toMessage(queueError, ''),
      });
    }
  };

  return (
    <div className="bg-background flex flex-col gap-3 p-3">
      <MatingAllocationTable
        matingKey={matingKey}
        matingName={mating.recordName ?? mating.id}
        allocation={mating.allocation}
      />
      <div className="flex max-w-2xl flex-col gap-2">
        <GradingMatrix
          data={gradingRows}
          title=""
          fullWidth
          showUnknownStage
          unknownStageLabel={t('onFarmImplant.fresh.unknownStage')}
          disabled={readOnly}
          onChange={(rows) => {
            setGradingRows(rows);
            setError(null);
          }}
          onClear={() => setGradingRows(emptyGradingRows())}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            disabled={readOnly || isCreating}
            onClick={() => void handleCreate()}
          >
            <CirclePlus className="size-3.5" />
            {t('onFarmImplant.fresh.createEmbryos', { count: embryoTotal })}
          </Button>
          {error && (
            <p role="alert" className="text-destructive text-xs">
              {error}
            </p>
          )}
        </div>
      </div>
      <MatingOutcomes
        matingKey={matingKey}
        allocation={mating.allocation}
        degenerates={
          sessionId ? (
            <DegeneratesField
              key={`${matingKey}:${mating.allocation?.onFarmDegenerates ?? ''}`}
              inputId={`degenerates-${matingKey}`}
              sessionId={sessionId}
              matingId={mating.id}
              matingName={mating.recordName ?? mating.id}
              saved={mating.allocation?.onFarmDegenerates}
              version={mating.allocation?.version}
              disabled={readOnly}
              autoCalculated={autoCalculateDegenerates}
            />
          ) : (
            <ReadOnlyOutcome
              id={`degenerates-${matingKey}`}
              label={t('onFarmImplant.fresh.degenerates.label')}
              value={mating.allocation?.onFarmDegenerates ?? 0}
            />
          )
        }
      />

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2 px-1">
          <h3 className="text-xs font-semibold text-gray-600">
            {t('onFarmImplant.fresh.createdEmbryos')} ({matingEmbryos.length})
          </h3>
          <select
            aria-label={t('onFarmImplant.fresh.filterByGrade')}
            value={gradeFilter}
            onChange={(event) =>
              setGradeFilter(event.target.value as GradeFilter)
            }
            className="border-input bg-background h-6 rounded-md border px-1.5 text-xs"
          >
            <option value="all">{t('onFarmImplant.fresh.allGrades')}</option>
            <option value="1">
              {t('onFarmImplant.fresh.grade', { grade: 1 })}
            </option>
            <option value="2">
              {t('onFarmImplant.fresh.grade', { grade: 2 })}
            </option>
            <option value="3">
              {t('onFarmImplant.fresh.grade', { grade: 3 })}
            </option>
          </select>
          <div className="ml-auto flex items-center gap-2">
            {selectedIds.length > 0 && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setSelectedIds([])}
              >
                {t('onFarmImplant.fresh.clearSelection')}
              </Button>
            )}
            <Button
              variant="outline"
              size="xs"
              disabled={readOnly || selectedIds.length === 0}
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="size-3" />
              {t('onFarmImplant.fresh.editSelected', {
                count: selectedIds.length,
              })}
            </Button>
            <Button
              variant="outline"
              size="xs"
              className="text-destructive"
              disabled={readOnly || selectedKeptEmbryos.length === 0}
              onClick={() => setEmbryoAction('discard')}
            >
              <Trash2 className="size-3" />
              {t('onFarmImplant.fresh.discard.button', {
                count: selectedKeptEmbryos.length,
              })}
            </Button>
            <Button
              variant="outline"
              size="xs"
              disabled={readOnly || selectedDiscardedEmbryos.length === 0}
              onClick={() => setEmbryoAction('recover')}
            >
              <Undo2 className="size-3" />
              {t('onFarmImplant.fresh.recover.button', {
                count: selectedDiscardedEmbryos.length,
              })}
            </Button>
            <Button
              variant="outline"
              size="xs"
              disabled={readOnly || !canSendToFreezing}
              title={
                selectedIds.length > 0 && !canSendToFreezing
                  ? t('onFarmImplant.onFarmFreezing.send.ineligibleHint')
                  : undefined
              }
              onClick={() => setFreezingOpen(true)}
            >
              {t('onFarmImplant.onFarmFreezing.send.button')}
            </Button>
            <Button
              variant="outline"
              size="xs"
              disabled={
                readOnly || selectedLoadedEmbryos.length === 0 || isQueueing
              }
              onClick={() => void handleQueue(false)}
            >
              {t('onFarmImplant.fresh.addSelectedToQueue', {
                count: selectedLoadedEmbryos.length,
              })}
            </Button>
            <Button
              size="xs"
              disabled={readOnly || loadedEmbryos.length === 0 || isQueueing}
              onClick={() => void handleQueue(true)}
            >
              {t('onFarmImplant.fresh.addAllToQueue')}
            </Button>
          </div>
        </div>
        <ContextMenu.Root
          onOpenChange={(open) => {
            if (!open) setMenuEmbryo(null);
          }}
        >
          <ContextMenu.Trigger>
            <DataTable
              rows={filteredEmbryos}
              columns={columns}
              getRowId={(embryo) => embryo.sessionEmbryoId}
              rowClassName={(embryo) =>
                cn(
                  embryo.grade === 2 &&
                    'odd:bg-amber-50 even:bg-amber-50 hover:bg-amber-100',
                  embryo.grade === 3 &&
                    'odd:bg-red-50 even:bg-red-50 hover:bg-red-100',
                )
              }
              onRowContextMenu={setMenuEmbryo}
              storageKey="on-farm-implant-fresh-embryo-table"
              reorderable
              columnChooser
              headerClassName="font-['Poppins',sans-serif] font-medium tracking-normal normal-case"
              fillWidth
              emptyMessage={t(
                matingEmbryos.length > 0
                  ? 'onFarmImplant.fresh.noEmbryosForGrade'
                  : 'onFarmImplant.fresh.noCreatedEmbryos',
              )}
            />
          </ContextMenu.Trigger>
          {menuEmbryo && (
            <ContextMenu.Portal>
              <ContextMenu.Positioner className="z-50 outline-none">
                <ContextMenu.Popup
                  className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} outline-none`}
                >
                  <div
                    className="ag-menu ag-ltr ag-popup-child"
                    style={{ position: 'static', maxHeight: 'none' }}
                  >
                    <div className="ag-menu-list">
                      {[
                        {
                          label: t('onFarmImplant.fresh.viewSessionHistory'),
                          id: menuEmbryo.sessionEmbryoId,
                        },
                        {
                          label: t('onFarmImplant.fresh.viewEmbryoHistory'),
                          id: menuEmbryo.embryoId,
                        },
                      ].map(({ label, id }) => (
                        <ContextMenu.Item
                          key={label}
                          className={({ highlighted }) =>
                            cn(
                              'ag-menu-option',
                              highlighted && 'ag-menu-option-active',
                            )
                          }
                          onClick={() =>
                            window.open(
                              `/audit/${id}`,
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                        >
                          <span className="ag-menu-option-part ag-menu-option-icon">
                            <i className="pi pi-history" />
                          </span>
                          <span className="ag-menu-option-part ag-menu-option-text">
                            {label}
                          </span>
                          <span className="ag-menu-option-part ag-menu-option-shortcut" />
                          <span className="ag-menu-option-part ag-menu-option-popup-pointer" />
                        </ContextMenu.Item>
                      ))}
                    </div>
                  </div>
                </ContextMenu.Popup>
              </ContextMenu.Positioner>
            </ContextMenu.Portal>
          )}
        </ContextMenu.Root>
      </div>
      {sessionId && (
        <FreshEmbryoActionDialog
          action={embryoAction ?? 'recover'}
          open={embryoAction !== null}
          onOpenChange={(open) => {
            if (!open) setEmbryoAction(null);
          }}
          sessionId={sessionId}
          embryos={
            embryoAction === 'discard'
              ? selectedKeptEmbryos
              : selectedDiscardedEmbryos
          }
          onDone={(updated) => {
            setSelectedIds([]);
            toast.add({
              data: { variant: 'success' },
              title: t(`onFarmImplant.fresh.${embryoAction}.successTitle`),
              description: t(`onFarmImplant.fresh.${embryoAction}.success`, {
                count: updated.length,
                straws: updated
                  .map((embryo) => embryo.strawNumber)
                  .filter((straw): straw is number => straw !== null)
                  .sort((a, b) => a - b)
                  .join(', '),
              }),
            });
          }}
        />
      )}
      {sessionId && (
        <SendToFreezingDialog
          open={freezingOpen}
          onOpenChange={setFreezingOpen}
          sessionId={sessionId}
          nameHeader={t('onFarmImplant.onFarmFreezing.mating')}
          embryos={selectedEmbryos.map((embryo) => ({
            sessionEmbryoId: embryo.sessionEmbryoId,
            embryoName: embryo.matingName,
            strawNumber: embryo.strawNumber,
            stage: embryo.stage,
            grade: embryo.grade,
            versions: embryo.versions,
          }))}
          onSent={() => setSelectedIds([])}
        />
      )}
      {sessionId && (
        <EditFreshEmbryosDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          sessionId={sessionId}
          sessionEmbryoIds={selectedIds}
          embryoIds={matingEmbryos
            .filter((embryo) => selectedIds.includes(embryo.sessionEmbryoId))
            .map((embryo) => embryo.embryoId)}
          strawCount={
            matingEmbryos.filter(
              (embryo) =>
                embryo.queueState !== 'Discarded' &&
                selectedIds.includes(embryo.sessionEmbryoId),
            ).length
          }
          onSaved={(count, nextStraw) => {
            setSelectedIds([]);
            if (nextStraw !== undefined) setNextStrawNumber(String(nextStraw));
            toast.add({
              data: { variant: 'success' },
              title: t('onFarmImplant.fresh.edit.successTitle'),
              description: t('onFarmImplant.fresh.edit.success', { count }),
            });
          }}
        />
      )}
    </div>
  );
};
